import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GRAUS_DE_PARENTESCO,
  ROTULO_GRAU_DE_PARENTESCO,
  ROTULO_TIPO_BENEFICIARIO,
  type GrauDeParentesco,
  type TipoBeneficiario,
} from '@reducee/contracts';
import { useState } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { CampoSelecao, CampoTexto } from './Campo.tsx';

export type BeneficiarioDoContato = {
  id: string;
  contatoId: string;
  contratoId: string | null;
  nome: string;
  tipo: TipoBeneficiario;
  documento: string | null;
  nascimento: string | null;
  parentesco: string | null;
  email: string | null;
  telefone: string | null;
  ativo: boolean;
};

export type ContratoParaVincular = {
  id: string;
  numero: string | null;
  produto: string;
  operadora: string | null;
  vigenciaInicio: string | null;
};

type Formulario = {
  nome: string;
  tipo: TipoBeneficiario;
  /** Vazio é "não informado"; o resto vem sempre da lista. */
  parentesco: GrauDeParentesco | '';
  nascimento: string;
  documento: string;
  email: string;
  telefone: string;
  contratoId: string;
};

const VAZIO: Formulario = {
  nome: '',
  tipo: 'TITULAR',
  parentesco: '',
  nascimento: '',
  documento: '',
  email: '',
  telefone: '',
  contratoId: '',
};

/**
 * Quem é coberto pelo Contrato.
 *
 * O bloco listava e parava aí: a operação cadastrava a vida na operadora e a
 * aplicação continuava dizendo "Nenhum beneficiário cadastrado". Os campos são
 * os que a operadora cobra numa inclusão — sem nascimento e parentesco não há
 * dependente, e sem CPF não há vida.
 */
export function BeneficiariosDoContato({
  contatoId,
  beneficiariosIniciais,
  contratos,
}: {
  contatoId: string;
  beneficiariosIniciais?: BeneficiarioDoContato[];
  contratos: ContratoParaVincular[];
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  /** `null` fecha o formulário, `'novo'` abre vazio, um id abre preenchido. */
  const [emEdicao, definirEmEdicao] = useState<string | null>(null);
  const [formulario, definirFormulario] = useState<Formulario>(VAZIO);
  const [erro, definirErro] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['beneficiarios', contatoId],
    queryFn: () => chamarApi<BeneficiarioDoContato[]>(`/contatos/${contatoId}/beneficiarios`),
    initialData: beneficiariosIniciais,
  });

  const invalidarConsultas = async (): Promise<void> => {
    await Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: ['beneficiarios', contatoId] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['visao-360', contatoId] }),
    ]);
  };

  const fechar = (): void => {
    definirEmEdicao(null);
    definirFormulario(VAZIO);
    definirErro(null);
  };

  const aoFalhar = (causa: unknown, alternativa: string): void => {
    definirErro(causa instanceof ErroDaApi ? causa.message : alternativa);
  };

  const salvar = useMutation({
    mutationFn: (dados: Formulario) => {
      // O parentesco é do dependente: o titular não é parente de si mesmo.
      const parentesco = dados.tipo === 'DEPENDENTE' ? dados.parentesco : '';
      const corpo = {
        tipo: dados.tipo,
        nome: dados.nome.trim(),
        parentesco,
        nascimento: dados.nascimento || null,
        documento: dados.documento.trim(),
        email: dados.email.trim(),
        telefone: dados.telefone.trim(),
        contratoId: dados.contratoId || null,
      };

      if (emEdicao && emEdicao !== 'novo') {
        return chamarApi<BeneficiarioDoContato>(`/beneficiarios/${emEdicao}`, {
          metodo: 'PATCH',
          corpo,
        });
      }

      return chamarApi<BeneficiarioDoContato>('/beneficiarios', {
        metodo: 'POST',
        // A criação não aceita nulo onde a correção aceita: lá o nulo apaga o
        // que existia, aqui o campo simplesmente nunca foi preenchido.
        corpo: {
          contatoId,
          ...corpo,
          nascimento: corpo.nascimento ?? undefined,
          contratoId: corpo.contratoId ?? undefined,
        },
      });
    },
    onMutate: () => definirErro(null),
    onSuccess: async () => {
      fechar();
      await invalidarConsultas();
    },
    onError: (causa: unknown) => aoFalhar(causa, 'Não foi possível salvar o Beneficiário.'),
  });

  const alternarAtivo = useMutation({
    mutationFn: (beneficiario: BeneficiarioDoContato) =>
      chamarApi<BeneficiarioDoContato>(`/beneficiarios/${beneficiario.id}`, {
        metodo: 'PATCH',
        corpo: { ativo: !beneficiario.ativo },
      }),
    onMutate: () => definirErro(null),
    onSuccess: invalidarConsultas,
    onError: (causa: unknown) => aoFalhar(causa, 'Não foi possível alterar o Beneficiário.'),
  });

  const abrirCorrecao = (beneficiario: BeneficiarioDoContato): void => {
    definirEmEdicao(beneficiario.id);
    definirErro(null);
    definirFormulario({
      nome: beneficiario.nome,
      tipo: beneficiario.tipo,
      // Cadastro anterior à lista pode trazer texto que não está nela: o
      // select abre em "A informar" em vez de inventar um grau.
      parentesco: grauConhecido(beneficiario.parentesco) ?? '',
      nascimento: beneficiario.nascimento?.slice(0, 10) ?? '',
      documento: beneficiario.documento ?? '',
      email: beneficiario.email ?? '',
      telefone: beneficiario.telefone ?? '',
      contratoId: beneficiario.contratoId ?? '',
    });
  };

  const lista = consulta.data ?? [];

  return (
    <div data-teste="visao-360-beneficiarios" className="rounded-xl bg-superficie p-5 ring-1 ring-linha">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-marinho">Beneficiários</h2>
        <button
          type="button"
          onClick={() => {
            if (emEdicao === 'novo') return fechar();
            definirEmEdicao('novo');
            definirErro(null);
            // Dependente não é o caso comum de quem abre o bloco vazio: o
            // primeiro cadastro de um Contrato é o titular.
            definirFormulario(VAZIO);
          }}
          className="rounded-[9px] border border-linha px-2.5 py-1 text-xs font-[750] text-marinho transition hover:bg-marinho-suave"
        >
          {emEdicao === 'novo' ? 'Cancelar' : '+ Beneficiário'}
        </button>
      </div>

      {emEdicao ? (
        <form
          data-teste="formulario-de-beneficiario"
          onSubmit={(evento) => {
            evento.preventDefault();
            salvar.mutate(formulario);
          }}
          className="mt-3 rounded-lg bg-marinho-suave p-3"
        >
          <div className="grid gap-2.5 sm:grid-cols-2">
            <CampoTexto
              rotulo="Nome"
              required
              maxLength={200}
              value={formulario.nome}
              onChange={(evento) =>
                definirFormulario({ ...formulario, nome: evento.target.value })
              }
              className="sm:col-span-2"
            />
            <CampoSelecao
              rotulo="Titular ou dependente"
              // Quem assina não é sempre quem usa: o Contrato pode ser do pai
              // e o plano, da filha. Sem este aviso, quem cadastra repete o
              // nome do contratante como titular por falta de saber.
              dica="Titular é quem usa o plano, e nem sempre é quem assina o contrato."
              value={formulario.tipo}
              onChange={(evento) =>
                definirFormulario({
                  ...formulario,
                  tipo: evento.target.value as TipoBeneficiario,
                })
              }
            >
              <option value="TITULAR">{ROTULO_TIPO_BENEFICIARIO.TITULAR}</option>
              <option value="DEPENDENTE">{ROTULO_TIPO_BENEFICIARIO.DEPENDENTE}</option>
            </CampoSelecao>
            {/*
              Parentesco só existe para dependente, e a operadora o exige. Sai
              de lista, e não da mão de quem digita: "esposa", "marido" e
              "companheira" viram três vínculos no nosso banco e um só no
              formulário da operadora.
            */}
            {formulario.tipo === 'DEPENDENTE' ? (
              <CampoSelecao
                rotulo="Grau de parentesco"
                value={formulario.parentesco}
                onChange={(evento) =>
                  definirFormulario({
                    ...formulario,
                    parentesco: evento.target.value as GrauDeParentesco | '',
                  })
                }
              >
                <option value="">A informar</option>
                {GRAUS_DE_PARENTESCO.map((grau) => (
                  <option key={grau} value={grau}>
                    {ROTULO_GRAU_DE_PARENTESCO[grau]}
                  </option>
                ))}
              </CampoSelecao>
            ) : null}
            <CampoTexto
              rotulo="Data de nascimento"
              type="date"
              value={formulario.nascimento}
              onChange={(evento) =>
                definirFormulario({ ...formulario, nascimento: evento.target.value })
              }
            />
            <CampoTexto
              rotulo="CPF"
              maxLength={32}
              value={formulario.documento}
              onChange={(evento) =>
                definirFormulario({ ...formulario, documento: evento.target.value })
              }
            />
            <CampoTexto
              rotulo="E-mail"
              type="email"
              maxLength={200}
              value={formulario.email}
              onChange={(evento) =>
                definirFormulario({ ...formulario, email: evento.target.value })
              }
            />
            <CampoTexto
              rotulo="Telefone"
              maxLength={40}
              value={formulario.telefone}
              onChange={(evento) =>
                definirFormulario({ ...formulario, telefone: evento.target.value })
              }
            />
            {/* Com um contrato só, o vínculo não é escolha — é o único que há. */}
            {contratos.length > 1 ? (
              <CampoSelecao
                rotulo="Contrato"
                value={formulario.contratoId}
                onChange={(evento) =>
                  definirFormulario({ ...formulario, contratoId: evento.target.value })
                }
                className="sm:col-span-2"
              >
                <option value="">Sem contrato definido</option>
                {contratos.map((contrato) => (
                  <option key={contrato.id} value={contrato.id}>
                    {rotuloDoContrato(contrato)}
                  </option>
                ))}
              </CampoSelecao>
            ) : null}
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={fechar}
              className="rounded-[9px] border border-linha px-3 py-1.5 text-[13px] font-[750] text-texto-suave transition hover:bg-superficie"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvar.isPending}
              className="rounded-[9px] bg-marinho px-3 py-1.5 text-[13px] font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar beneficiário'}
            </button>
          </div>
        </form>
      ) : null}

      {erro ? (
        <p role="alert" className="mt-2 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      {consulta.isPending ? <p className="mt-2 text-sm text-texto-suave">Carregando…</p> : null}
      {consulta.isError ? (
        <p role="alert" className="mt-2 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          Não foi possível carregar os Beneficiários.
        </p>
      ) : null}
      {!consulta.isPending && !consulta.isError && lista.length === 0 ? (
        <p className="mt-2 text-sm text-texto-suave">Nenhum beneficiário cadastrado.</p>
      ) : null}

      {lista.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {lista.map((beneficiario) => (
            <li
              key={beneficiario.id}
              className={`rounded-lg px-3 py-2 text-sm ${
                beneficiario.ativo ? 'bg-marinho-suave' : 'bg-marinho-suave/50 text-texto-suave'
              }`}
            >
              <div className="flex justify-between gap-2">
                <span className="font-medium">{beneficiario.nome}</span>
                <span className="text-xs">
                  {ROTULO_TIPO_BENEFICIARIO[beneficiario.tipo]}
                  {beneficiario.ativo ? '' : ' · desativado'}
                </span>
              </div>
              {/* Uma linha por assunto: quem é, e por onde se fala com ele. */}
              {linhaDeIdentidade(beneficiario) ? (
                <p className="text-xs text-texto-suave">{linhaDeIdentidade(beneficiario)}</p>
              ) : null}
              {linhaDeContato(beneficiario) ? (
                <p className="text-xs text-texto-suave">{linhaDeContato(beneficiario)}</p>
              ) : null}
              {/* Com um contrato só, dizer qual é não acrescenta nada; com dois,
                  a mesma titular aparece duas vezes e só isto as distingue. */}
              {contratos.length > 1 && beneficiario.contratoId ? (
                <p className="text-xs text-texto-suave">
                  {rotuloDoContratoPorId(contratos, beneficiario.contratoId)}
                </p>
              ) : null}
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => abrirCorrecao(beneficiario)}
                  aria-label={`Corrigir ${beneficiario.nome}`}
                  className="rounded-[9px] border border-linha px-2.5 py-1 text-xs font-[750] transition hover:bg-superficie"
                >
                  Corrigir
                </button>
                <button
                  type="button"
                  disabled={alternarAtivo.isPending}
                  onClick={() => alternarAtivo.mutate(beneficiario)}
                  aria-label={`${beneficiario.ativo ? 'Desativar' : 'Reativar'} ${beneficiario.nome}`}
                  className="rounded-[9px] border border-linha px-2.5 py-1 text-xs font-[750] text-texto-suave transition hover:bg-superficie disabled:opacity-60"
                >
                  {beneficiario.ativo ? 'Desativar' : 'Reativar'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function linhaDeIdentidade(beneficiario: BeneficiarioDoContato): string {
  return [
    rotuloDoParentesco(beneficiario.parentesco),
    beneficiario.nascimento
      ? `nasc. ${new Date(beneficiario.nascimento).toLocaleDateString('pt-BR')}`
      : null,
    beneficiario.documento,
  ]
    .filter(Boolean)
    .join(' · ');
}

function grauConhecido(valor: string | null): GrauDeParentesco | null {
  return GRAUS_DE_PARENTESCO.find((grau) => grau === valor) ?? null;
}

/** O rótulo da lista; o texto livre de antes dela continua aparecendo como está. */
function rotuloDoParentesco(valor: string | null): string | null {
  const grau = grauConhecido(valor);
  return grau ? ROTULO_GRAU_DE_PARENTESCO[grau] : valor;
}

function linhaDeContato(beneficiario: BeneficiarioDoContato): string {
  return [beneficiario.telefone, beneficiario.email].filter(Boolean).join(' · ');
}

function rotuloDoContratoPorId(contratos: ContratoParaVincular[], id: string): string | null {
  const contrato = contratos.find((candidato) => candidato.id === id);
  return contrato ? rotuloDoContrato(contrato) : null;
}

function rotuloDoContrato(contrato: ContratoParaVincular): string {
  const vigencia = contrato.vigenciaInicio
    ? ` · desde ${new Date(contrato.vigenciaInicio).toLocaleDateString('pt-BR')}`
    : '';
  // O número quase nunca vem preenchido; produto e operadora é o que distingue
  // os dois contratos de uma empresa que tem saúde e odonto.
  const nome = [contrato.produto, contrato.operadora, contrato.numero].filter(Boolean).join(' · ');
  return `${nome}${vigencia}`;
}
